import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Mail, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error();
      toast.success("You're on the list — practical tips, no spam.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-ink text-white grain">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          <div>
            <div className="flex items-center gap-2.5 mb-6">
              <img src="/assets/icon_mark.png" alt="Avora" className="h-10 w-10 rounded-xl" />
              <span className="font-display font-bold text-2xl tracking-tight text-white">avora</span>
            </div>
            <p className="text-white/60 text-sm leading-relaxed max-w-xs">
              A Sydney digital studio helping small businesses get online — fast, affordable, and jargon-free.
            </p>
            <div className="flex gap-3 mt-6">
              {[
                { icon: Instagram, href: CONTACT.instagram, label: "Instagram", id: "instagram" },
                { icon: Facebook, href: CONTACT.facebook, label: "Facebook", id: "facebook" },
                { icon: Linkedin, href: CONTACT.linkedin, label: "LinkedIn", id: "linkedin" },
              ].map(({ icon: Icon, href, label, id }) => (
                <a
                  key={id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${id}`}
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-indigo transition-colors duration-300"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-display font-semibold text-sm uppercase tracking-widest text-white/40 mb-5">Explore</h4>
            <ul className="space-y-3 text-sm">
              {[
                { to: "/services", label: "Services" },
                { to: "/pricing", label: "Pricing" },
                { to: "/work", label: "Our Work" },
                { to: "/about", label: "About Avora" },
                { to: "/blog", label: "Tips & Guides" },
                { to: "/contact", label: "Contact" },
                { to: "/pay", label: "Pay an Invoice" },
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-white/70 hover:text-mint transition-colors duration-200">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-sm uppercase tracking-widest text-white/40 mb-5">Say hello</h4>
            <ul className="space-y-4 text-sm">
              <li>
                <a href={`mailto:${CONTACT.email}`} data-testid="footer-email" className="flex items-center gap-3 text-white/70 hover:text-mint transition-colors duration-200">
                  <Mail className="w-4 h-4 shrink-0" /> {CONTACT.email}
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/70" data-testid="footer-location">
                <MapPin className="w-4 h-4 shrink-0" /> {CONTACT.location}
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-sm uppercase tracking-widest text-white/40 mb-5">Small business tips</h4>
            <p className="text-white/60 text-sm mb-4">One practical digital tip a month. No spam, ever.</p>
            <form onSubmit={subscribe} className="flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@business.com.au"
                data-testid="newsletter-email-input"
                className="flex-1 min-w-0 bg-white/10 border border-white/10 rounded-full px-4 py-2.5 text-sm placeholder:text-white/40 focus:outline-none focus:border-mint transition-colors duration-200"
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="newsletter-submit-button"
                className="w-11 h-11 shrink-0 rounded-full bg-indigo flex items-center justify-center hover:bg-mint hover:text-ink transition-colors duration-300 disabled:opacity-50"
                aria-label="Subscribe"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/40">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} Avora Business Solutions. Sydney, Australia.</p>
          <p>Built fast with AI, finished by humans.</p>
        </div>
      </div>
    </footer>
  );
};
